import { Cell, EditorUi, Graph, ActionKeys, ToolboxGroup, ToolboxItem } from "@edp-pmf/grapheditor";
import { WfGraph } from "./WfGraph";
import { ProcessHeader, BizActivity, UserActivity, OperationActivity } from "@gsp-wf/wf-process-model";
import { WfSidebar } from "./WfSidebar";
import { WfConstants } from "./WfConstants";
import { mxEvent, mxResources } from "@edp-pmf/mxgraph-ts";
import { UserActivityPropertyKeys } from "./wf-cell/property-keys/UserActivityPropertyKeys";
import { WfExclusiveGatewayCell } from "./wf-cell/WfExclusiveGatewayCell";
import { AifCreationBillActivityCell } from "./wf-cell/AifCreationBillActivityCell";
import { AutoActivityCell } from "./wf-cell/AutoActivityCell";
import { CallActivitySubProcessCell } from "./wf-cell/CallActivitySubProcessCell";
import { IntermediateCatchEventImplCell } from "./wf-cell/IntermediateCatchEventImplCell";
import { MessageActivityCell } from "./wf-cell/MessageActivityCell";
import { NotifyActivityCell } from "./wf-cell/NotifyActivityCell";
import { OperationActivityCell } from "./wf-cell/OperationActivityCell";
import { StartActivityCell } from "./wf-cell/StartActivityCell";
import { UserActivityCell } from "./wf-cell/UserActivityCell";
import { WfParallelGatewayCell } from "./wf-cell/WfParallelGatewayCell";
import { WfEndEventCell } from "./wf-cell/WfEndEventCell";
import { WfInclusiveGatewayCell } from "./wf-cell/WfInclusiveGatewayCell";
import { WfProcessEditorComponent } from "../wf-process-editor.component";
import { BatchSetPropertiesComponent } from "../views/batch-set-properties/batch-set-properties.component";
import { BsModalService, ModalOptions } from "@farris/ui-modal";
import { ComponentFactoryResolver, Injector } from "@angular/core";
import { ProcessDesignerUIState } from "../app/process-designer.uistate";

export class WfEditorUi extends EditorUi {
  editor: WfProcessEditorComponent;
  graph: WfGraph;
  sidebar: WfSidebar;
  time: string;
  bizCategoryId: string;
  action: string;
  mode: string;
  scene: string;
  procType: string;
  procExtProps: any;
  formFormats: any;
  generalConfig: { enableTransfer: boolean; enableNotify: boolean; enableAddSignBehind: boolean; enableAddSignFront: boolean; } = {
    enableTransfer: true,
    enableNotify: false,
    enableAddSignBehind: false,
    enableAddSignFront: false,
  };

  zoomBar: HTMLDivElement;

  uistate: ProcessDesignerUIState;

  constructor(time: string, action: string, container?: HTMLElement, private injector?: Injector,
    private modalService?: BsModalService,
    private resolver?: ComponentFactoryResolver, eventSource?: any, importLinks: boolean = true,) {
    super(container, eventSource, importLinks);
    let rootPath = '';
    try {
      rootPath = (window as any).gspframeworkService.common.getBasePath.get();
    } catch (error) {
    }
    mxResources.add(rootPath + "/platform/runtime/common/web/@gsp-wf/wf-process-editor/resources/wf-process-editor");
    this.showToolbar = false;
    this.time = time;
    this.action = action;
  }

  createActions() {
    const actions = super.createActions();

    // 只有设计时编辑状态下才启用删除动作
    if (!(this.action === 'edit' && this.time !== 'rt')) {
      const deleteAction = actions.getAction(ActionKeys.pmf_delete);

      deleteAction.isEnabled = () => false;
    }

    return actions;
  }

  createToolbox() {
    const toolbox = super.createToolbox();

    const g1 = new ToolboxGroup();
    g1.name = mxResources.get('basicElements');
    g1.items.push(
      new ToolboxItem(StartActivityCell.CLR_TYPE_ID),
      new ToolboxItem(WfExclusiveGatewayCell.CLR_TYPE_ID),
      new ToolboxItem(WfParallelGatewayCell.CLR_TYPE_ID),
      // 暂时隐藏包容网关
      new ToolboxItem(WfInclusiveGatewayCell.CLR_TYPE_ID),
      new ToolboxItem(UserActivityCell.CLR_TYPE_ID),
      new ToolboxItem(OperationActivityCell.CLR_TYPE_ID),
      new ToolboxItem(NotifyActivityCell.CLR_TYPE_ID),
      new ToolboxItem(CallActivitySubProcessCell.CLR_TYPE_ID),
      new ToolboxItem(AutoActivityCell.CLR_TYPE_ID),
      new ToolboxItem(IntermediateCatchEventImplCell.CLR_TYPE_ID),
      new ToolboxItem(MessageActivityCell.CLR_TYPE_ID),
      new ToolboxItem(AifCreationBillActivityCell.CLR_TYPE_ID),
      new ToolboxItem(WfEndEventCell.CLR_TYPE_ID),
    );

    toolbox.groups = [g1];

    return toolbox;
  }

  createGraphInstance(): Graph {
    const graph = new WfGraph(this, this.diagramContainer);

    if (this.action === 'view' || this.time === 'rt' || this.action === 'assignView') {
      graph.setCellsLocked(true);
    } else {
      graph.setDropEnabled(true);
      graph.setConnectable(true);
    }
    //TODO：右键查看只显示在审批节点上
    graph.popupMenuHandler.autoExpand = true;
    graph.popupMenuHandler.factoryMethod = (menu, cell, evt) => {
      const actList = graph.getChildCells(graph.getDefaultParent())
      if (cell != null
        && cell.clrTypeId.indexOf('wf.UserActivity') > -1
        && actList.findIndex(a => a.id === cell.getId()) > -1) {
        menu.addItem(mxResources.get('batchCopyProp'), null, () => {
          const compFactory = this.resolver.resolveComponentFactory(BatchSetPropertiesComponent);
          const compRef = compFactory.create(this.injector);
          compRef.instance.activityModel = cell.flowElement;
          const modalConfig: ModalOptions = {
            title: mxResources.get('batchCopyProp'),
            width: 512,
            height: 465,
            showButtons: true,
            showMaxButton: false,
            buttons: compRef.instance.footer
          };
          const dialog = this.modalService.show(compRef, modalConfig);
          compRef.instance.cancel.subscribe($event => dialog.close());
          compRef.instance.submit.subscribe((data: string) => {
            dialog.close();
          });
        });
      }
    };
    return graph;
  }

  createSidebar(): WfSidebar {
    return new WfSidebar(this, this.sidebarContainer);
  }

  show(): void {
    super.show();

    if (this.action === 'view' || this.action === 'assignView') {
      const span = this.createSpan('');
      span.style.fontSize = '14px';
      span.style.color = 'white';
      span.style.borderRadius = '20px';
      span.style.padding = '4px 20px';
      span.style.background = 'rgb(199, 194, 187)';
      const v = document.createTextNode(mxResources.get('readOnly'));
      span.appendChild(v);
      const d = this.createDiv('');
      d.style.position = 'absolute';
      d.style.top = '10px';
      d.style.right = '10px';
      d.style.zIndex = "2";
      d.appendChild(span);
      this.container.appendChild(d);
    }

    const buttonIn = document.createElement('a');
    buttonIn.setAttribute('href', 'javascript:void(0);');
    let buttonInImg = document.createElement('img');
    buttonIn.style.marginRight = '10px';
    buttonInImg.src = WfConstants.IMAGE_PATH + '/zoomIn.svg';
    buttonInImg.style.display = "inline-block";
    buttonInImg.style.verticalAlign = "middle";
    buttonIn.appendChild(buttonInImg);
    mxEvent.addListener(buttonIn, 'click', (evt: PointerEvent) => {
      this.actions.getAction(ActionKeys.pmf_zoomIn).execute(evt);
    });

    const buttonOut = document.createElement('a');
    buttonOut.setAttribute('href', 'javascript:void(0);');
    let buttonOutImg = document.createElement('img');
    buttonOutImg.src = WfConstants.IMAGE_PATH + '/zoomOut.svg';
    buttonOutImg.style.display = "inline-block";
    buttonOutImg.style.verticalAlign = "middle";
    buttonOut.appendChild(buttonOutImg);
    mxEvent.addListener(buttonOut, 'click', (evt: PointerEvent) => {
      this.actions.getAction(ActionKeys.pmf_zoomOut).execute(evt);
    });

    const zoomBar = this.createDiv('zoomBar');
    zoomBar.style.position = 'absolute';
    zoomBar.style.zIndex = "2";
    let t = 10;
    if (this.showMenubar) {
      t += this.menubarHeight;
    }
    if (this.showToolbar) {
      t += this.toolbarHeight;
    }
    zoomBar.style.top = t + 'px';
    zoomBar.style.left = (this.showSidebar ? this.sidebarSplitPosition + this.sidebarSplitSize : 0) + 10 + "px";

    zoomBar.appendChild(buttonIn);
    zoomBar.appendChild(buttonOut);
    this.container.appendChild(zoomBar);
    this.zoomBar = zoomBar;
  }

  sidebarSplitOnChange(value: number): void {
    super.sidebarSplitOnChange(value);

    // 分割条移动后更新缩放条位置
    if (this.zoomBar) {
      this.zoomBar.style.left = (this.showSidebar ? this.sidebarSplitPosition + this.sidebarSplitSize : 0) + 10 + "px";
    }
  }

  /**
   * 创建带指定样式类的span
   * @param classname
   * @returns
   */
  createSpan(classname: string): HTMLSpanElement {
    let elt = document.createElement('span');
    elt.className = classname;

    return elt;
  }

  /**
   * 加载业务活动
   * @param bizActivities
   */
  loadBizActivities(bizActivities: BizActivity[]) {
    this.sidebar && this.sidebar.addBizActPalette(bizActivities);
  }

  /**
   * 批量复制属性
   * @param obj
   */
  batchCopyProperty(obj: any) {
    // 放弃不需要的参数
    const ids = obj.value.targetNodeID;
    obj.propertyValue = obj.value.messageNotice;
    // todo：多个活动具有同名属性时，如何更合理定义PropertyKey？
    obj.propertyID = UserActivityPropertyKeys.messageNotice;
    for (const id of ids) {
      const cell = this.graph.getCellByFlowElementId(id);
      if (cell) {
        cell.updateProps(obj);
        this.graph.refresh();
      }
    }
  }

  /**
 * 批量设置属性
 * @param obj
 */
  batchSetProperty(obj: any) {
    for (const v of obj) {
      switch (v.type) {
        case 'addSignfront':
          const afId = v.actis;
          obj.propertyValue = v.config;
          obj.propertyID = UserActivityPropertyKeys.AddSignFrontAble;
          for (const id of afId) {
            const cell = this.graph.getCellByFlowElementId(id.Id);
            if (cell) {
              cell.updateProps(obj);
              this.graph.refresh();
            }
          }
          break;
        case 'addSignBehind':
          const abId = v.actis;
          obj.propertyValue = v.config;
          obj.propertyID = UserActivityPropertyKeys.AddSignBehindAble;
          for (const id of abId) {
            const cell = this.graph.getCellByFlowElementId(id.Id);
            if (cell) {
              cell.updateProps(obj);
              this.graph.refresh();
            }
          }
          break;
        case 'transfer':
          const trId = v.actis;
          obj.propertyValue = v.config;
          obj.propertyID = UserActivityPropertyKeys.transferScope;
          for (const id of trId) {
            const cell = this.graph.getCellByFlowElementId(id.Id);
            if (cell) {
              cell.updateProps(obj);
              this.graph.refresh();
            }
          }
          break;
        case 'notify':
          const ntId = v.actis;
          obj.propertyValue = v.config;
          obj.propertyID = UserActivityPropertyKeys.NotifyAble;
          for (const id of ntId) {
            const cell = this.graph.getCellByFlowElementId(id.Id);
            if (cell) {
              cell.updateProps(obj);
              this.graph.refresh();
            }
          }
          break;
        // 驳回规则需要特殊处理：子流程和信号的赋简版
        case 'backWardRule':
          const bwId = v.actis;
          obj.propertyValue = v.config;
          obj.propertyID = UserActivityPropertyKeys.BackWardRule;
          for (const id of bwId) {
            const cell = this.graph.getCellByFlowElementId(id.Id);
            if (cell) {
              cell.updateProps(obj);
              this.graph.refresh();
            }
          }
          break;
        case 'participantOptions':
          const poId = v.actis;
          obj.propertyValue = v.config;
          obj.propertyID = UserActivityPropertyKeys.ParticipantOptions;
          for (const id of poId) {
            const cell = this.graph.getCellByFlowElementId(id.Id);
            if (cell) {
              cell.updateProps(obj);
              this.graph.refresh();
            }
          }
      }
    }
  }

  /**
   * 创建新流程
   * @param newProcInfo
   * @param bizCmps
   */
  createBpmnModel(newProcInfo: any, bizCmps?: any) {
    this.graph.createBpmnModelWithProcInfo(newProcInfo, bizCmps);

  }

  /**
   * 打开流程
   * @param content
   */
  openBpmnModel(content: string) {
    this.graph.loadBpmnModel(content);
  }

  /**
   * 获取属性配置，刷新属性框
   * @param obj
   * @returns
   */
  refreshProperty(obj: any) {
    const cell = this.graph.getCellByFlowElementId(obj.Id);
    return this.getFarrisPropConfig([cell]);
  }

  /**
   * 获取用户活动
   * @returns
   */
  getActivities() {
    const list = [];
    const bpmnModel = this.graph.bpmnModel;
    const flowDict = bpmnModel.DefaultProcess.GetFlowElements();
    Object.keys(flowDict).forEach((key) => {
      const flowElt = flowDict[key];
      if (flowElt.ClrTypeID === UserActivity.Clr_Type_ID
        || flowElt.ClrTypeID === OperationActivity.Clr_Type_ID) {
        list.push(flowElt);
      }
    });
    return list;
  }

  /**
   * 获取结束事件
   * @returns
   */
  getEndEvents() {
    const userActArray = [];
    const bpmnModel = this.graph.bpmnModel;
    const flowDict = bpmnModel.DefaultProcess.GetFlowElements();
    Object.keys(flowDict).forEach((key) => {
      const flowElt = flowDict[key];
      if (flowElt.ClrTypeID === 'bpmn.EndEvent' || flowElt.ClrTypeID === UserActivity.Clr_Type_ID
        || flowElt.ClrTypeID === OperationActivity.Clr_Type_ID) {
        userActArray.push(flowElt);
      }
    });
    return userActArray;
  }

  /**
   * 获取知会事件
   * @returns
   */
  getNotifyEvents() {
    const list = [];
    const bpmnModel = this.graph.bpmnModel;
    const flowDict = bpmnModel.DefaultProcess.GetFlowElements();
    Object.keys(flowDict).forEach((key) => {
      const flowElt = flowDict[key];
      if (flowElt.ClrTypeID === 'wf.NotifyActivity') {
        list.push(flowElt);
      }
    });
    return list;
  }

  /**
   * 获取消息事件
   * @returns
   */
  getMessageEvents() {
    const list = [];
    const bpmnModel = this.graph.bpmnModel;
    const flowDict = bpmnModel.DefaultProcess.GetFlowElements();
    Object.keys(flowDict).forEach((key) => {
      const flowElt = flowDict[key];
      if (flowElt.ClrTypeID === 'bpmn.MessageEvent') {
        list.push(flowElt);
      }
    });
    return list;
  }
  /**
   * 获取子流程和信号捕获节点
   * @returns
   */
  getSubAndCatchEvents() {
    const list = [];
    const bpmnModel = this.graph.bpmnModel;
    const flowDict = bpmnModel.DefaultProcess.GetFlowElements();
    Object.keys(flowDict).forEach((key) => {
      const flowElt = flowDict[key];
      if (flowElt.ClrTypeID === 'wf.CallActivitySubProcess' ||
      flowElt.ClrTypeID === 'wf.IntermediateCatchEventImpl') {
        list.push(flowElt);
      }
    });
    return list;
  }
  /**
   * 获取启动方式
   * @returns
   */
  getStartMode() {
    let startMode = null;
    const bpmnModel = this.graph.bpmnModel;
    const extendElements = bpmnModel.DefaultProcess.GetExtensionElements();
    if (extendElements.length > 0) {
      for (const e of extendElements) {
        if (e instanceof ProcessHeader) {
          startMode = e.startMode;
        }
      }
    }
    return startMode;
  }
}
